import { IconGridItem } from "@/shared/types/IconGrid.interface";
import { cn } from "@/shared/utils";
import { BenefitSection } from "@/shared/utils/IconUtils";
import React from "react";

interface SectionGridProps {
    sections: SectionGridItem[];
    isPlatinum?: boolean;
    hasBreedRules?: boolean;
}

export interface SectionGridItem {
    section: BenefitSection;
    items: (IconGridItem & { highlight?: boolean })[];
    highlight?: boolean;
}

const SectionGrid: React.FC<SectionGridProps> = ({ sections, isPlatinum, hasBreedRules = false }) => {
    const shouldShowRecommendation = hasBreedRules;

    return (
        <div
            className={cn(
                shouldShowRecommendation ? "gap-x-6" : "gap-x-4",
                "grid w-full grid-cols-1 gap-y-1 xs:gap-y-2 sm:grid-cols-2  sm:gap-y-1 md:grid-cols-1 md:gap-y-2 lg:grid-cols-2"
            )}
        >
            {sections.map(sectionData => (
                <div
                    key={sectionData.section.id}
                    className={cn(
                        sectionData.highlight &&
                            cn(
                                shouldShowRecommendation
                                    ? "-mx-2 mb-2 w-[calc(100%+1rem)] rounded-lg bg-background-success px-2"
                                    : "-mx-4 -mt-3 h-fit bg-background-success px-[15px] pb-4 pt-3 sm:-m-2 sm:mb-0 sm:rounded-lg sm:p-2 md:-mx-4 md:-mt-3 md:mb-0 md:rounded-none md:px-[15px] md:pb-4 md:pt-3 lg:-m-2 lg:rounded-lg lg:p-2"
                            ),
                        sectionData.section.id === "vaccines_titers" && sections.length === 4 && isPlatinum && "translate-y-2"
                    )}
                    data-testid={sectionData.highlight ? `benefit-highlighted-${sectionData.section.id}` : `section-${sectionData.section.id}`}
                >
                    <div className="flex items-center gap-1">
                        <div
                            className={cn(
                                shouldShowRecommendation ? "-ml-1" : "-ml-0",
                                "flex size-9 items-center justify-center xs:size-12 sm:size-9 md:size-12 lg:size-9 xl:size-12"
                            )}
                        >
                            {sectionData.section.icon}
                        </div>
                        <h3
                            className={cn("text-sm font-bold text-content-secondary xs:text-base sm:text-sm md:text-base lg:text-sm xl:text-base", {
                                "dark:text-content-success": sectionData.highlight
                            })}
                        >
                            {sectionData.section.title}
                        </h3>
                    </div>
                    <ul className={cn("space-y-1", shouldShowRecommendation ? "pl-3 xs:pl-3 md:pl-3 lg:pl-3 xl:pl-3" : "pl-6 xs:pl-8 sm:pl-6 md:pl-8 lg:pl-6 xl:pl-8")}>
                        {sectionData.items.map((item, i) => (
                            <li key={i} className="mb-1 flex justify-between text-xs xs:text-sm sm:text-xs md:text-sm lg:text-xs xl:text-sm">
                                <div
                                    className={cn("flex w-full items-start justify-between rounded-md", item.highlight && "-mx-5 w-[calc(100%+1.7rem)] bg-background-success px-2")}
                                >
                                    <div
                                        className={cn("flex items-start text-content-secondary", {
                                            "-ml-3": !item.highlight && shouldShowRecommendation,
                                            "dark:text-content-success": sectionData.highlight
                                        })}
                                    >
                                        <span className={cn(shouldShowRecommendation ? "mr-2" : "mr-4 sm:mr-3 md:mr-4 lg:mr-3 xl:mr-4")}>&bull;</span>
                                        <span>{item.text}</span>
                                    </div>
                                    <span className="ml-2 text-content-primary">${item.amount?.toFixed(0)}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
};

export default SectionGrid;
